import { GetExecutions, RunJob, RunJobForAllSchoolsInDistrict } from '../../api/executions-endpoint';
import { ChangeEvent, MouseEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Execution } from '../../types/execution';
import Autocomplete from '@mui/material/Autocomplete';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import useConfigStyles from '../../components/useConfigStyles';
import { Link } from 'react-router-dom';
import { getLinkToExecutionSummaryPage } from '../../navigator/links';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import TextInput from '../../components/TextInput';
import { canRunJobs, canViewJobsForAnyDistrictAndSchool, canViewCertificationsForAnyDistrictAndSchool, canRunJobsForAnySchoolInAssignedDistrict, canViewCertificationsForAnySchoolInAssignedDistrict, canViewJobsForAnySchoolInAssignedDistrict, getUserProfile, isDistrictUser } from '../../utilities/AuthService';
import TablePaginationActions from '../../components/TablePaginationActions';
import SnackbarAlert from '../../components/Alerts';
import DataCertificationInfo from 'components/data-certifications/DataCertificationInfo';
import './executions.css';
import { GetDistrictsForState, GetSchoolsForDistrictsForState } from 'api/edfi-district-school-endpoints';
import { EdfiDistrict } from 'types/edfi-district';
import { EdfiSchool } from 'types/edfi-school';
import { AlertCircleIcon, CheckMarkCircleIcon } from 'resources/icons';

function ExecutionsLandingPage(): JSX.Element {
  const classes = useConfigStyles();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [responseMessage, setResponseMessage] = useState('');
  const [filteredJobName, setFilteredJobName] = useState('');
  const [schoolYears, setSchoolYears] = useState(['All']);
  const [filteredSchoolYear, setFilteredSchoolYear] = useState('All');
  const [districtId, setDistrictId] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [queriedDistrictId, setQueriedDistrictId] = useState('');
  const [queriedDistrictName, setQueriedDistrictName] = useState('');
  const [queriedDistrictOrgId, setQueriedDistrictOrgId] = useState('');
  const [queriedSchoolId, setQueriedSchoolId] = useState('');
  const [queriedSchoolName, setQueriedSchoolName] = useState('');
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [jobLoading, setJobLoading] = useState(false);
  const currentUser = getUserProfile();
  const currentUserCanRunJobs = canRunJobs();
  const currentUserIsDistrictUser = isDistrictUser();
  const currentUserCanViewJobsForAnyDistrictAndSchool = canViewJobsForAnyDistrictAndSchool();
  const currentUserCanViewCertificationsForAnyDistrictAndSchool = canViewCertificationsForAnyDistrictAndSchool();
  const currentUserCanViewJobsForAnySchoolInAssignedDistrict = canViewJobsForAnySchoolInAssignedDistrict();
  const currentUserCanRunJobsForAnySchoolInAssignedDistrict = canRunJobsForAnySchoolInAssignedDistrict();
  const [notify, toggleNotification] = useState(false);
  const [districtOptions, setDistrictOptions] = useState<EdfiDistrict[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<EdfiSchool[]>([]);
  const [districtsLoadError, setDistrictsLoadError] = useState(false);
  const [needToReloadDataCertificationInfo, setNeedToReloadDataCertificationInfo] = useState(false);
  const [showDataCertificationInfo, setShowDataCertificationInfo] = useState(false);

  const handleJobNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilteredJobName(event.target.value);
    setPage(0);
  };

  const getQueriedSchoolOption = () => {
    if (queriedSchoolId) {
      return schoolOptions.find((option) => option.SchoolId === queriedSchoolId) || null;
    }
    return null;
  }

  const getQueriedDistrictOption = () => {
    if (queriedDistrictId) {
      return districtOptions.find((option) => option.DistrictId === queriedDistrictId) || null;
    }
    return null;
  }

  const handleDistrictIdChange = (event: ChangeEvent<{}>, value: EdfiDistrict | null) => {
    if (value?.DistrictId) {
      setDistrictId(value.DistrictId);
    }
    if (!value) {
      setDistrictId('');
    }
  };
  const selectedDistrictOption = districtOptions.find((option) => option.DistrictId === districtId) || null;
  const districtNoOptionText = () => {
    if (districtOptions.length == 0) return 'No districts found.';
    if (districtsLoadError) return 'Error loading districts - please refresh this page or contact an admin.';
    return 'Loading...';
  };

  const handleSchoolIdChange = (event: ChangeEvent<{}>, value: EdfiSchool | null) => {
    if (value?.SchoolId) {
      setSchoolId(value.SchoolId);
    }
    if (!value) {
      setSchoolId('');
    }
  };
  const selectedSchoolOption = schoolOptions.find((option) => option.SchoolId === schoolId) || null;

  const handleSchoolYearChange = (event: SyntheticEvent<Element, Event>, value: string) => {
    setFilteredSchoolYear(value);
  };

  const fetchDistricts = async () => {
    const response = await GetDistrictsForState();
    if (response.success) {
      setDistrictOptions(response.data!);
    } else {
      setDistrictsLoadError(true);
    }
  };

  const fetchSchoolsForDistrict = async () => {
    if (currentUserCanViewJobsForAnyDistrictAndSchool) {
      if (selectedDistrictOption !== null) {
        const response = await GetSchoolsForDistrictsForState(districtId);
        if (response.data) {
          setSchoolOptions(response.data);
        }
      }
    }
    if (currentUserCanViewJobsForAnySchoolInAssignedDistrict) {
      const response = await GetSchoolsForDistrictsForState(currentUser?.DistrictId || '');
      if (response.data) {
        setSchoolOptions(response.data);
      }
    }
  };

  const fetchJobs = async () => {
    const currentDistrictId = districtId;
    const currentSchoolId = schoolId;

    let queryParams = '';
    if (filteredSchoolYear != 'All') {
      queryParams += `schoolYear=${filteredSchoolYear}&`;
    }
    if (filteredJobName != '') {
      queryParams += `jobNameFilter=${filteredJobName}&`;
    }
    if (currentDistrictId != '') {
      queryParams += `districtId=${currentDistrictId}&`;
    }
    if (currentSchoolId != '') {
      queryParams += `schoolId=${currentSchoolId}&`;
    }

    if (currentUserCanViewJobsForAnyDistrictAndSchool && !currentDistrictId && !currentSchoolId) {
      return;
    }

    const response = await GetExecutions(queryParams);
    if (response.success && response.data) {
      setQueriedDistrictId(currentDistrictId || currentUser?.DistrictId || '');
      setQueriedDistrictName(selectedDistrictOption?.Name || currentUser?.DistrictName || '');
      setQueriedDistrictOrgId(selectedDistrictOption?.OrganizationId || currentUser?.OrganizationId || '');
      setQueriedSchoolId(currentSchoolId || currentUser?.SchoolId || '');
      setQueriedSchoolName(selectedSchoolOption?.Name || currentUser?.SchoolName || '');

      setExecutions(response.data);
      if (response.data.length > 0) {
        let years = ['All'];
        years = years.concat(response.data[0].SchoolYears.filter((value, index, self) => self.indexOf(value) === index));
        setSchoolYears(years);
      } else {
        setSchoolYears(['All', filteredSchoolYear].filter((value, index, self) => self.indexOf(value) === index));
      }
    } else {
      setResponseMessage(response.message || '');
      toggleNotification(true);
    }
  };

  const runJob = useCallback(
    async (jobName: string) => {     
      setJobLoading(true);
      const currentDistrictId = districtId;
      const currentSchoolId = schoolId;
      let queryParams = '';
      if (currentDistrictId != '') {
        queryParams += `districtId=${currentDistrictId}&`;
      }
      if (currentSchoolId != '') {
        queryParams += `schoolId=${currentSchoolId}`;
      }
      const response = await RunJob(jobName, queryParams).then();
      if (response.success && response.data) {
        setResponseMessage('Job executed successfully.');
        toggleNotification(true);
        fetchJobs();
      } else {
        setResponseMessage(response.message || '');
        toggleNotification(true);
      }  
      setJobLoading(false);
    },
    [fetchJobs],
  );

  const runJobForAllSchoolsInDistrict = useCallback(
    async (jobName: string) => {    
      setJobLoading(true);
      const response = await RunJobForAllSchoolsInDistrict(jobName).then();
      if (response.success && response.data) {
        setResponseMessage('Job executed successfully.');
        toggleNotification(true);
        fetchJobs();
      } else {
        setResponseMessage(response.message || '');
        toggleNotification(true);
      }
      setJobLoading(false);
    },
    [fetchJobs],
  );

  useEffect(() => {
    fetchJobs();
    if (currentUserCanViewJobsForAnyDistrictAndSchool) {
      fetchDistricts();
    }
    if (currentUserCanViewJobsForAnySchoolInAssignedDistrict) {
      setDistrictId(currentUser?.DistrictId || '');
    }
  }, []);

  useEffect(() => {
    if (currentUserCanViewJobsForAnyDistrictAndSchool || currentUserCanViewJobsForAnySchoolInAssignedDistrict) {
      fetchSchoolsForDistrict();
    }
  }, [districtId]);

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleCloseSnackbar = () => {
    toggleNotification(false);
  };

  const refreshJobs = async () => {
    await fetchJobs();
    setNeedToReloadDataCertificationInfo(true);
  }

  useEffect(() => { 
    const userCanViewAllCertsAndHasQueriedForSomeSpecificOnes = currentUserCanViewCertificationsForAnyDistrictAndSchool && (queriedDistrictId.length > 0 || queriedSchoolId.length > 0);
    const shouldShowDataCertificationInfo = userCanViewAllCertsAndHasQueriedForSomeSpecificOnes || !currentUserCanViewCertificationsForAnyDistrictAndSchool;
    setShowDataCertificationInfo(shouldShowDataCertificationInfo);
  }, [currentUserCanViewCertificationsForAnyDistrictAndSchool, queriedDistrictId, queriedSchoolId])

  return (
    <Container className={classes.container}>
      <div className='executions-header-row'>
        <div className='executions-header-title'>
          <h1 style={{marginBottom: '0px'}}>Validation Jobs</h1> 
          <div>
            {queriedDistrictId || queriedSchoolId ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {queriedDistrictId && (
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <b>District:&nbsp;</b> {(queriedDistrictOrgId || '')  + ' ' + (queriedDistrictName || '')}
                  </div>
                )}{' '}
                {queriedSchoolId && (
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <b>Building:&nbsp;</b> {(getQueriedSchoolOption()?.BuildingId || '') + ' ' + (queriedSchoolName || '')}
                  </div>
                )}
              </div>
            ) : (
              <div>Provide District / Building and click Refresh Jobs to view jobs</div>
            )}
          </div>
        </div>

        {showDataCertificationInfo && 
          <div className='executions-header-certification-container'>
            <DataCertificationInfo districtId={queriedDistrictId} schoolId={queriedSchoolId} needReload={needToReloadDataCertificationInfo} setNeedReload={setNeedToReloadDataCertificationInfo}/>
          </div>
        }
      </div>
      <Grid container justifyContent='space-around'>
        <Grid item>
          <TextInput name='search' label='Search by Job Name' value={filteredJobName} onChange={handleJobNameChange} />
        </Grid>
        <Grid item xs={1}>
          <Autocomplete
              value={filteredSchoolYear}
              onChange={(event, value) => {
                const syntheticEvent = {
                  target: { name: 'schoolYear', value },
                } as ChangeEvent<HTMLInputElement>;
                handleSchoolYearChange(syntheticEvent, value || '');
              }}
              options={schoolYears}
              getOptionLabel={(option) => ''+option}
              noOptionsText={'All'}
              renderInput={(params) => <TextField {...params} label='School Year' />}
              filterOptions={(options, { inputValue }) => {
                return options.filter((option) => 

                  inputValue='All' || (''+option).toLowerCase().includes(inputValue.toLowerCase())
              );
              }}
            />

        </Grid>
        {currentUserCanViewJobsForAnyDistrictAndSchool && (
          <>
            <Grid item xs={3}>
              <Autocomplete
                value={selectedDistrictOption}
                onChange={handleDistrictIdChange}
                options={districtOptions}
                getOptionLabel={(option) => (option.OrganizationId || '') + ' ' + option.Name}
                noOptionsText={districtNoOptionText()}
                renderInput={(params) => <TextField {...params} label='District' />}
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option) => option.Name.toLowerCase().includes(inputValue.toLowerCase()) || option.DistrictId.toLowerCase().includes(inputValue.toLowerCase()) || option.OrganizationId.toLowerCase().includes(inputValue.toLowerCase()));
                }}
              />
            </Grid>
          </>
        )}
        {(currentUserCanViewJobsForAnyDistrictAndSchool || currentUserCanViewJobsForAnySchoolInAssignedDistrict) && (
          <>
            <Grid item xs={3}>
            <Autocomplete
              value={selectedSchoolOption}
              onChange={handleSchoolIdChange}
              options={schoolOptions}
              getOptionLabel={(option) => (option.BuildingId || '') + ' ' + option.Name}
              noOptionsText={selectedDistrictOption !== null ? `No schools for ${selectedDistrictOption.Name}` : `Select a district to see building options`}
              renderInput={(params) => <TextField {...params} label='Building' />}
              filterOptions={(options, { inputValue }) => {
                return options.filter((option) => option.Name.toLowerCase().includes(inputValue.toLowerCase()) || option.SchoolId.toLowerCase().includes(inputValue.toLowerCase()) || option.BuildingId.toLowerCase().includes(inputValue.toLowerCase()));
              }}
            />
          </Grid>
          </>)
        }

        <Grid item>
          <Button className={classes.button} onClick={refreshJobs} variant='contained' color='secondary'>
            Refresh Jobs
          </Button>
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label='Job Results / Executions Table'>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell>Job Name</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Results</TableCell>
              {currentUserCanRunJobs && <TableCell>Run Job</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {executions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((execution) => (
              <TableRow key={execution.Job} className={classes.tableRow}>
                <TableCell component='th' scope='row'>
                  {execution.Job}
                </TableCell>
                <TableCell component='th' scope='row'>
                  {execution.ExecutedAt ? new Date(execution.ExecutedAt).toLocaleString() : 'Never'}
                </TableCell>
                <TableCell component='th' scope='row'>
                  {execution.ExecutedAt ? 
                  <>{ execution.Status === 'No Violations' ?
                    <div style={{'display': 'flex', 'flexDirection': 'row', 'maxHeight': '2rem'}}>
                      <div className='no-violations-checkmark'>
                        <CheckMarkCircleIcon/>
                      </div>
                      &nbsp;
                      <>All records passed validation</>
                    </div>
                    :<>{ execution.Status === 'Violations' ?
                      <div style={{'display': 'flex', 'flexDirection': 'row', 'maxHeight': '2rem'}}>
                        <div className='violations-icon'>
                          <AlertCircleIcon/>
                        </div>
                        &nbsp;
                        <>Records found</>
                      </div>
                      : execution.Status
                      }
                    </>
                    }
                  </>
                  : 'Never run'}
                </TableCell>
                <TableCell component='th' scope='row'>
                  {execution.ExecutedAt 
                    ? <Link to={getLinkToExecutionSummaryPage(execution.Job, execution.Log)}>View Results</Link>
                    : <div>No Results</div>
                  }
                </TableCell>
                {currentUserCanRunJobs && (
                  <TableCell component='th' scope='row'>
                  { jobLoading 
                    ? <Button variant='contained' color='primary' disabled style={{width: '200px'}}>
                    Loading
                    </Button>
                    : <>
                      { currentUserIsDistrictUser && currentUserCanRunJobsForAnySchoolInAssignedDistrict && !queriedSchoolId
                      ? <Button onClick={() => runJobForAllSchoolsInDistrict(execution.Job)} variant='contained' color='primary' style={{width: '200px'}}>
                          Run Job For All Schools in District
                        </Button>
                      : <Button onClick={() => runJob(execution.Job)} variant='contained' color='primary' style={{width: '200px'}}>
                          Run Job
                        </Button>
                      }
                    </>
                  }
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[rowsPerPage]}
                colSpan={3}
                count={executions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'Jobs per page',
                  },
                  native: true,
                }}
                onPageChange={handleChangePage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
        <SnackbarAlert open={notify} message={responseMessage} handleClose={handleCloseSnackbar} />
      </TableContainer>
    </Container>
  );
}

export default ExecutionsLandingPage;
